import {isSettingFileChildInputType} from './settingFileFieldMappings'

/** Bootstrap width for a sequenced item inside the shared flex row. */
export type ChannelSettingSequenceRowSpan = 'half' | 'full'

/** Slot IS the legacy row column; inner nodes must not repeat col-* / mt-4. */
export const CHANNEL_SETTING_SEQUENCE_ROW_CLASS: Record<ChannelSettingSequenceRowSpan, string> = {
  half: 'col-12 col-sm-6 mt-4',
  full: 'col-12 mt-4',
}

export const CHANNEL_SETTING_SEQUENCE_SLOT_BASE_CLASS = 'channel-setting-sequence-slot'

/** gy-4 only — outer section `.row.p-4` already provides horizontal padding. */
export const CHANNEL_SETTING_SEQUENCE_ROW_CONTAINER_CLASS =
  'row gy-4 channel-setting-sequence-row'

export const CHANNEL_SETTING_SEQUENCE_SLOT_ALIGN_CONTROL_CLASS =
  'channel-setting-sequence-slot--align-control'

type ChildSettingLike = {
  input_type?: string | null
  field_mappings?: unknown[] | null
}

type MappingLike = {
  isDefault?: boolean | null
}

type SequenceFieldWrapperOptions = {
  /** Preserve Bootstrap half-columns inside linked child panels (sequence main row still strips cols). */
  insideLinkedPanel?: boolean
}

const BOOTSTRAP_COLUMN_TOKEN_PATTERN = /^col(?:-\d+|-[a-z]+-\d+)?$/i

const stripSequenceInnerLayoutClasses = (legacyClass: string): string =>
  legacyClass
    .split(/\s+/)
    .filter((token) => token && !BOOTSTRAP_COLUMN_TOKEN_PATTERN.test(token) && token !== 'mt-4')
    .join(' ')

/** Legacy col wrapper inset; half-slots own padding when sequence order is on. */
export const getSequenceFieldShellStyle = (): undefined => undefined

/** Independent/default mapping shell inside a sequence slot (no ps-4/pe-4 when sequence is on). */
export const getSequenceIndependentMappingShellClass = (options?: {
  useAdvancedGridCell?: boolean
}): string => {
  if (options?.useAdvancedGridCell) {
    return 'mapping py-2'
  }

  return 'mapping py-2'
}

/**
 * Legacy inner column class when sequence order is off.
 * When on, strip Bootstrap column width + mt-4 so the sequence slot owns layout.
 */
export const getSequenceInnerColumnClass = (legacyClass = 'col-12 col-sm-6'): string =>
  stripSequenceInnerLayoutClasses(legacyClass)

/** Independent mapping outer row: slot owns half-row width when sequence is on. */
export const getIndependentMappingWrapperClass = (
  options?: SequenceFieldWrapperOptions
): string => {
  if (options?.insideLinkedPanel) {
    return 'col-12 col-sm-6'
  }

  return 'row mb-1 w-100'
}

/** MappingInput half-width wrappers outside the sequence slot. */
export const getSequenceMappingInputWidthClass = (usesHalfWidth: boolean): string => {
  if (!usesHalfWidth) {
    return ''
  }

  return 'w-100'
}

/** Wrapper class for field containers inside a sequence slot. */
export const getSequenceFieldWrapperClass = (
  legacyClass = 'col-12 col-sm-6 mt-4',
  options?: SequenceFieldWrapperOptions
): string => {
  if (options?.insideLinkedPanel) {
    return stripSequenceInnerLayoutClasses(legacyClass.replace(/\bmt-4\b/g, '').trim()) || 'col-12 col-sm-6'
  }

  return stripSequenceInnerLayoutClasses(legacyClass)
}

/** CustomInput containerClasses when rendered inside a sequence slot. */
export const getSequenceCustomInputContainerClass = (
  legacyClass = 'col-12 col-sm-6 mt-4',
  options?: SequenceFieldWrapperOptions
): string => getSequenceFieldWrapperClass(legacyClass, options)

/** Child settings on the shared number line. */
export const getChildSettingSequenceRowSpan = (
  child: ChildSettingLike | null | undefined
): ChannelSettingSequenceRowSpan => {
  if (isSettingFileChildInputType(child?.input_type)) {
    return 'full'
  }

  return 'half'
}

/** Top-level mappings on the shared number line. */
export const getMappingSequenceRowSpan = (
  mapping: MappingLike | null | undefined,
  options?: {isNestedBooleanBlock?: boolean}
): ChannelSettingSequenceRowSpan => {
  if (options?.isNestedBooleanBlock) {
    return 'full'
  }

  return mapping?.isDefault ? 'half' : 'full'
}

export const getChannelSettingSequenceSlotClass = (
  rowSpan: ChannelSettingSequenceRowSpan = 'half',
  modifierClass?: string
): string =>
  [
    CHANNEL_SETTING_SEQUENCE_ROW_CLASS[rowSpan],
    CHANNEL_SETTING_SEQUENCE_SLOT_BASE_CLASS,
    `channel-setting-sequence-slot--${rowSpan}`,
    modifierClass,
  ]
    .filter(Boolean)
    .join(' ')

/** @deprecated Use getChannelSettingSequenceSlotClass. */
export const getChannelSettingSequenceRowClass = getChannelSettingSequenceSlotClass
